import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import useAuthStore from '../hooks/useAuthStore';
import QuizModule from './QuizModule';

export default function QuizPage() {
  const navigate = useNavigate();
  const { theme } = useAuthStore();
  const [selectedSubject, setSelectedSubject] = useState('physics');
  const [selectedCurriculum, setSelectedCurriculum] = useState(theme || 'icse');

  const isICSE = selectedCurriculum === 'icse';
  const primaryColor = isICSE ? 'icse-teal' : 'isc-coral';
  const secondaryColor = isICSE ? 'icse-cyan' : 'isc-purple';

  const subjects = isICSE 
    ? ['Physics', 'Chemistry', 'Mathematics', 'Biology', 'English']
    : ['Physics', 'Chemistry', 'Mathematics', 'Biology'];

  return (
    <div className="min-h-screen w-full text-white antialiased bg-[linear-gradient(180deg,_#0d0b18_0%,_#16122c_50%,_#0b0914_100%)] relative overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3"
          >
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br from-${primaryColor}-500 to-${secondaryColor}-500 flex items-center justify-center text-white font-bold text-xl`}>
              S
            </div>
            <h1 className="text-2xl font-bold text-white">SolveICSE</h1>
          </button>

          <div className="flex items-center gap-4">
            {/* Curriculum Toggle */}
            <div className="flex items-center gap-2 bg-white/10 rounded-lg p-1">
              <button
                onClick={() => setSelectedCurriculum('icse')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  isICSE 
                    ? 'bg-gradient-to-r from-icse-teal-500 to-icse-cyan-500 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                ICSE (Class 10)
              </button>
              <button
                onClick={() => setSelectedCurriculum('isc')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  selectedCurriculum === 'isc' 
                    ? 'bg-gradient-to-r from-isc-coral-500 to-isc-purple-500 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                ISC (Class 12)
              </button>
            </div>

            <button
              onClick={() => navigate('/profile')}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>Profile</span>
            </button>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-5xl font-bold text-white mb-4">
              Practice Quiz
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Test your knowledge with {isICSE ? 'ICSE Class 10' : 'ISC Class 12'} practice questions
            </p>
          </motion.div>

          {/* Subject Selection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-8"
          >
            <h3 className="text-xl font-bold text-white mb-4">Select Subject</h3>
            <div className="flex flex-wrap gap-3">
              {subjects.map((subject) => (
                <button
                  key={subject}
                  onClick={() => setSelectedSubject(subject.toLowerCase())}
                  className={`px-6 py-3 rounded-lg font-medium transition-all ${
                    selectedSubject === subject.toLowerCase()
                      ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white'
                      : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  {subject}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Quiz Module */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="max-w-2xl mx-auto"
          >
            <QuizModule subject={selectedSubject} curriculum={selectedCurriculum} />
          </motion.div>
        </div>
      </main>
    </div>
  );
}
